import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TurnstileWidget from './TurnstileWidget';
import ThankYouModal from './ThankYouModal';
import smsService from '../services/smsService';
import emailService from '../services/emailService';
import { leadsAPI } from '../services/api/leadsAPI';
import { FiUser, FiMail, FiPhone, FiHome, FiDollarSign, FiMessageSquare, FiSend, FiCheck, FiX } from 'react-icons/fi';

const LeadFormShed = ({ shed = null, inquiryType = 'general', isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    inquiryType: inquiryType,
    message: shed 
      ? `I'm interested in the ${shed.name || `${shed.size} ${shed.shedType}`}.` 
      : '',
    preferredContactMethod: 'email',
    preferredContactTime: '',
    budgetMin: '',
    budgetMax: '',
    financingNeeded: false,
    siteReady: '',
    deliveryAddress: '',
    estimatedTimeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const leadData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        lead_type: formData.inquiryType,
        lead_source: 'website',
        message: formData.message,
        preferred_contact_method: formData.preferredContactMethod,
        preferred_contact_time: formData.preferredContactTime,
        budget_min: formData.budgetMin ? parseFloat(formData.budgetMin) : null,
        budget_max: formData.budgetMax ? parseFloat(formData.budgetMax) : null,
        financing_needed: formData.financingNeeded,
        // Shed-specific fields
        shed_id: shed?.id,
        shed_name: shed?.name,
        shed_size: shed?.size,
        shed_type: shed?.shedType,
        shed_price: shed?.salePrice || shed?.price,
        site_ready: formData.siteReady,
        delivery_address: formData.deliveryAddress,
        estimated_timeline: formData.estimatedTimeline
      };

      await leadsAPI.addLead(leadData);
      
      // Send email notification
      try {
        await emailService.sendLeadNotification({
          ...leadData,
          firstName: formData.firstName,
          lastName: formData.lastName,
          leadType: formData.inquiryType
        });
      } catch (emailErr) {
        console.warn('Email notification failed:', emailErr);
      }
      
      // Send SMS if phone provided
      if (formData.phone) {
        try {
          await smsService.sendLeadResponse(formData.phone, {
            name: shed?.name || 'shed',
            size: shed?.size || '',
            type: shed?.shedType || 'storage shed'
          });
        } catch (smsErr) {
          console.warn('SMS failed:', smsErr);
        }
      }
      
      setIsSubmitting(false);
      setSubmitted(true);
      setShowThankYou(true);
      
      // Auto close after success
      setTimeout(() => {
        setShowThankYou(false);
        if (onClose) onClose();
      }, 3000);
    } catch (err) {
      console.error('Error submitting lead:', err);
      setIsSubmitting(false);
      alert('There was an error submitting your inquiry. Please try again or call us directly.');
    }
  };

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'quote', label: 'Request a Quote' },
    { value: 'customization', label: 'Customization Options' },
    { value: 'financing', label: 'Financing Questions' },
    { value: 'delivery', label: 'Delivery Information' },
    { value: 'schedule_visit', label: 'Schedule a Visit' }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {inquiryType === 'quote' ? 'Request a Quote' :
                 inquiryType === 'customization' ? 'Ask About Customization' :
                 'Contact Us'}
              </h2>
              {shed && (
                <p className="text-sm text-gray-500">{shed.name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <div className="relative">
                    <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Doe"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="(817) 555-1234"
                  />
                </div>
              </div>

              {/* Inquiry Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Type</label>
                <select
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  {inquiryTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Site Ready */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Is your site prepared for a shed?</label>
                <select
                  name="siteReady"
                  value={formData.siteReady}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes, site is ready</option>
                  <option value="no">No, need site prep info</option>
                  <option value="unsure">Not sure</option>
                </select>
              </div>

              {/* Timeline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">When do you need the shed?</label>
                <select
                  name="estimatedTimeline"
                  value={formData.estimatedTimeline}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select timeline...</option>
                  <option value="asap">As soon as possible</option>
                  <option value="1-2weeks">1-2 weeks</option>
                  <option value="1month">Within a month</option>
                  <option value="2-3months">2-3 months</option>
                  <option value="just_looking">Just looking</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <div className="relative">
                  <FiMessageSquare className="absolute left-3 top-3 text-gray-400" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                    placeholder="Tell us about your project or ask any questions..."
                  />
                </div>
              </div>

              {/* Financing */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="financingNeeded"
                  checked={formData.financingNeeded}
                  onChange={handleChange}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-700">I'm interested in financing options</span>
              </label>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend className="w-5 h-5" />
                    Send Inquiry
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to be contacted about your inquiry.
              </p>
            </form>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-4">
                We've received your inquiry and will get back to you shortly.
              </p>
              <button
                onClick={onClose}
                className="text-emerald-600 font-medium hover:text-emerald-700"
              >
                Close
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadFormShed;
